import Boundary from './Boundary.js';
import Sprite from '../ui/Sprite.js';
import { imageStore } from '../images.js';
import Pokemon from './Pokemon.js';
import { globalPokemonDataStore } from '../OverworldAnimation.js';

export default class StaticEncounter extends Boundary {
    serialize() {
        console.log(`Serializing ${this.constructor.name}`);
        return {
            position: this.position,
            level: this.level,
            id: this.id,
            pokemonId: this.pokemonId,
            identifier: this.identifier,
            encountered: this.encountered,
        };
    }

    static resolveIdFromSlug(slug) {
        for (const [key, entry] of Object.entries(globalPokemonDataStore)) {
            if (entry.slug === slug || entry.name?.toLowerCase() === slug?.toLowerCase()) {
                return parseInt(key, 10);
            }
        }
        return undefined;
    }

    static deserialize(data) {
        const id = data.id ?? StaticEncounter.resolveIdFromSlug(data.pokemonId);
        return new StaticEncounter({
            position: data.position,
            level: data.level,
            id: id,
            pokemonId: data.pokemonId,
            identifier: data.identifier,
            encountered: data.encountered || false,
        });
    }

    constructor({ position, level, id, pokemonId, identifier = null, encountered = false }) {
        super({ position });
        this.level = level;
        this.id = id;
        this.pokemonId = pokemonId;
        this.identifier = identifier;
        this.interactionPriority = 10;
        this.encountered = encountered;
        this._canInteract = true;
        this.sprite = new Sprite({
            position: {
                x: this.position.x - 8,
                y: this.position.y - 8,
                z: this.position.z,
            },
            image: imageStore.get(`img/pokemon/static/${this.pokemonId}.png`),
        });
        // Initialize pokemon asynchronously
        this.pokemonPromise = Pokemon.create({
            id: this.id,
            level: this.level,
        })
            .then((pokemon) => {
                pokemon.staticEncounter = this;
                this.pokemonInstance = pokemon;
                return pokemon;
            })
            .catch((error) => {
                console.error('Failed to create pokemon for StaticEncounter:', error);
                throw error;
            });
        this.pokemonInstance = null;
    }

    async init() {
        // Ensure pokemon is initialized
        if (!this.pokemonInstance) {
            this.pokemonInstance = await this.pokemonPromise;
        }
        return this.pokemonInstance;
    }

    get pokemon() {
        if (this.pokemonInstance) {
            return this.pokemonInstance;
        }
        // If pokemon hasn't been initialized yet, throw an error
        // This ensures callers use async/await properly
        throw new Error(
            'Pokemon not initialized. Call await staticEncounter.init() first or use getPokemon() method.'
        );
    }

    async getPokemon() {
        return await this.pokemonPromise;
    }

    reset() {
        this.encountered = false;
        if (this.pokemonInstance) {
            this.pokemonInstance.heal();
        }
    }

    conclude() {
        this.position.z = 7;
        this.sprite.visible = false;
        this.encountered = true;
    }

    canInteract() {
        return !this.encountered;
    }

    interact() {
        // Interaction will be handled by the state machine
        // This marks that the encounter has been triggered
        this.encountered = true;
    }

    draw() {
        if (this.encountered) return;
        const effectivePos = this.effectivePosition();
        this.sprite.position = {
            x: effectivePos.x - 8,
            y: effectivePos.y - 8,
            z: this.position.z,
        };
        this.sprite.draw();
    }
}
